import { useEffect, useState, useMemo, useRef } from "react";
import api from "../../api/axios";
import Swal from "sweetalert2";
import ContactDrawer from "./ContactDrawer";
import "../../assets/css/Common/stats_cards.css";
import "../../assets/css/Contact/ContactsList.css";
import ContactCreateModal from "./CreateContact";
import DynamicTable from "../Common/DynamicTable";
import { contactColumns } from "./ContactsColumns";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";
import {
  FiUsers,
  FiBriefcase,
  FiTrendingUp,
  FiUserPlus,
} from "react-icons/fi";

const DEFAULT_CARDS = {
  total_contacts: 0,
  total_organizations: 0,
  open_leads: 0,
  open_deals: 0,
};

export default function ContactsList() {
  const [contacts, setContacts] = useState([]);
  const [cards, setCards] = useState(DEFAULT_CARDS);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (filter = dateFilter) => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = buildDateFilterParams(filter);
      const hasParams = Object.keys(params).length > 0;
      const [contactsRes, cardsRes] = await Promise.all([
        hasParams ? api.get("/contact/list", { params }) : api.get("/contact/list"),
        hasParams ? api.get("/contact/cards", { params }) : api.get("/contact/cards"),
      ]);

      if (currentRequestId !== requestIdRef.current) return;

      setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
      setCards(cardsRes.data || DEFAULT_CARDS);
    } catch (error) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error("Failed loading contacts data", error);
      setContacts([]);
      setCards(DEFAULT_CARDS);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchContacts = (filter) => fetchData(filter);
  const fetchCards = (filter) => fetchData(filter);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    fetchData(newFilter);
  };



  useEffect(() => {
    // console.log("drawerOpen changed:", drawerOpen);
  }, [drawerOpen]);



  const openContact = async (id) => {
    const res = await api.get(`/contact/get/${id}`);

    // console.log("API response:", res);
    // console.log("Response data:", res.data);

    setSelectedContact({
      ...res.data,
      id: Number(id),
    });

    setDrawerOpen(true);
  };

  // console.log("ContactsList rendered", contacts);


const organizations = useMemo(() => {
  return [
    ...new Set(
      contacts.flatMap((contact) =>
        (contact.organizations || []).map((org) => org.name)
      )
    ),
  ].sort();
}, [contacts]);

  const roles = useMemo(() => {
    return [
      ...new Set(
        contacts
          .map((c) => c.designation)
          .filter(Boolean)
      ),
    ];
  }, [contacts]);


  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id]
    );
  };


const handleSelectAll = () => {
  if (selectedIds.length === sortedContacts.length) {
    setSelectedIds([]);
  } else {
    setSelectedIds(sortedContacts.map((c) => c.id));
  }
};


  const bulkDelete = async () => {
    const result = await Swal.fire({
      title: "Delete Contacts?",
      text: `${selectedIds.length} will be deleted`,
      icon: "warning",
      showCancelButton: true,
    });

    if (!result.isConfirmed) return;

    await api.post("/contact/bulk-delete", {
      contact_ids: selectedIds,
    });

    await fetchContacts();
    setSelectedIds([]);
  };

  const filtered = contacts.filter((c) => {
    const safeSearch = (search || "").toLowerCase().trim();
    const fullName = `${c.first_name ?? ""} ${c.second_name ?? ""}`.toLowerCase();
    const orgNames = (c.organizations || [])
      .map((org) => org.name || "")
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      !safeSearch ||
      fullName.includes(safeSearch) ||
      (c.email || "").toLowerCase().includes(safeSearch) ||
      (c.phone || "").toLowerCase().includes(safeSearch) ||
      (c.designation || "").toLowerCase().includes(safeSearch) ||
      orgNames.includes(safeSearch) ||
      String(c.id || "").includes(safeSearch);

const matchesOrganization =
  !organizationFilter ||
  (c.organizations || []).some(
    (org) => org.name === organizationFilter
  );

    const matchesRole =
      !roleFilter ||
      c.designation === roleFilter;

    return (
      matchesSearch &&
      matchesOrganization &&
      matchesRole
    );
  });

const filters = [
  {
    name: "organization",
    type: "autocomplete",
    value: organizationFilter,
    onChange: setOrganizationFilter,
    options: organizations,
    placeholder: "All Organization...",
  },
];

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const getSortValue = (row, key) => {
    switch (key) {
      case "name":
        return `${row.first_name ?? ""} ${row.second_name ?? ""}`.trim();

      case "organization":
  return row.organizations
    ?.map((org) => org.name)
    .join(", ");

    case "open_leads":
  return row.open_leads;

case "open_deals":
  return row.open_deals;

      default:
        return row[key];
    }
  };

  const sortedContacts = useMemo(() => {
    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortConfig.direction === "asc"
          ? aVal - bVal
          : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filtered, sortConfig]);

  const handleSortChange = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };
const user = JSON.parse(sessionStorage.getItem("user"));
const canDelete =
  user?.role !== "Sales Executive" &&
  user?.role !== "Sales Manager";


  return (
    <div className="main-wrapper">

      <div className="page-header">
        <div>
          <h2>Contacts</h2>
        </div>
        <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
      </div>

      {/* STATS */}
      <div className={`stats-grid stats-grid-4${loading ? " refreshing" : ""}`}>
        <div className="stat-card">
          <div className="stat-icon blue">
            <FiUsers />
          </div>
          <div>
            <p>Total Contacts</p>
            <h2>{cards?.total_contacts ?? 0}</h2>

          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <FiBriefcase />
          </div>
          <div>
            <p>Total Organizations</p>
            <h2>{cards?.total_organizations ?? 0}</h2>

          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <FiUserPlus />
          </div>
          <div>
            <p>Active Leads</p>
            <h2>{cards?.open_leads ?? 0}</h2>

          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon gold">
            <FiTrendingUp />
          </div>
          <div>
            <p>Active Deals</p>
            <h2>{cards?.open_deals ?? 0}</h2>

          </div>
        </div>
      </div>


      {/* TABLE */}
      <div className="table-card">
        <DynamicTable
         
        columns={contactColumns({
  selectedIds,
  handleSelectAll,
  handleSelect,
  totalRows: sortedContacts.length,
})}
          onRowClick={(id) => openContact(id)}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search contacts..."
          filters={filters}
          selectedIds={selectedIds}
          selectedCount={selectedIds.length}
          onBulkDelete={bulkDelete}
          onAdd={() => setIsCreateOpen(true)}
          addButtonText="Add Contact"
          data={sortedContacts}
          sortConfig={sortConfig}
            canDelete={canDelete}
          onSortChange={handleSortChange}
          storageKey="contacts_columns"
        />
      </div>

      {/* DRAWER */}
      <ContactDrawer
        contact={selectedContact}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        refreshContacts={fetchContacts}
      />

      {/* CREATE MODAL */}
      {isCreateOpen && (
        <ContactCreateModal
          onClose={() => setIsCreateOpen(false)}
          refreshContacts={fetchContacts}
        />
      )}
    </div>
  );
}